import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Fades a section in when it scrolls into view. With
 * prefers-reduced-motion the content is shown immediately (CSS also
 * disables the transition), so nothing ever stays hidden.
 */
export function Reveal({ children, as: Tag = 'section', id, label }: { children: ReactNode; as?: 'section' | 'div'; id?: string; label?: string }) {
  const ref = useRef<HTMLElement | null>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShown(true);
          io.disconnect();
        }
      },
      { rootMargin: '0px 0px -10% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref as never}
      id={id}
      aria-label={label}
      className="reveal"
      data-shown={shown ? 'true' : 'false'}
    >
      {children}
    </Tag>
  );
}
